import { useState, useEffect } from "react";
import { apiGet } from "../api/client";
import { PageHeader } from "../components/ui/Primitives";
import { fmtKst } from "../lib/format";
import { useWebSocket } from "../hooks/useWebSocket";
import { V2RiskControlsResponse } from "../api/types";
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Zap,
  CheckCircle2,
  Layers,
  Percent,
} from "lucide-react";

export default function Risk() {
  const { latest } = useWebSocket();
  const [events, setEvents] = useState<any[]>([]);
  const [trackingError, setTrackingError] = useState<any>(null);
  const [v2Controls, setV2Controls] = useState<V2RiskControlsResponse | null>(null);
  const [profile, setProfile] = useState<{ available: boolean; strategy_profile: string; strategy_params: Record<string, Record<string, number>> } | null>(null);
  const [hours, setHours] = useState(24);
  const [loading, setLoading] = useState(false);

  const fetchRiskData = async () => {
    setLoading(true);
    try {
      const [evRes, trRes, v2Res, profileRes] = await Promise.all([
        apiGet<any>(`/risk/events?hours=${hours}&limit=100`).catch(() => ({ events: [] })),
        apiGet<any>(`/risk/tracking-error?hours=${hours}`).catch(() => null),
        apiGet<V2RiskControlsResponse>("/risk/v2-controls").catch(() => null),
        apiGet<any>("/positions/profile").catch(() => null),
      ]);
      setEvents(evRes.events || []);
      setTrackingError(trRes);
      setV2Controls(v2Res);
      setProfile(profileRes);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiskData();
  }, [hours]);

  const killSwitch = latest.risk_state?.kill_switch ?? v2Controls?.guards.kill_switch;
  const macroGuard = latest.risk_state?.macro_guard ?? v2Controls?.guards.macro_guard ?? {};
  const vkospiGuard = v2Controls?.guards.vkospi_guard ?? {};

  const formatPrice = (val: any) => {
    if (val === null || val === undefined || isNaN(Number(val))) return "—";
    return `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const trend = profile?.available && profile.strategy_profile === "trend";
  const params = profile?.strategy_params.C1;
  const hard = v2Controls?.hard_safeguards;
  const util = v2Controls?.live_utilization;

  return (
    <div>
      <PageHeader
        icon={<Shield size={24} color="var(--accent)" />}
        title="리스크 관리"
        description="현재 운용 프로필의 리스크 규칙과 비상 제어 상태를 확인합니다."
        actions={
          <button className="btn-secondary" onClick={fetchRiskData} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : ""} />
            새로고침
          </button>
        }
      />

      {trend && <section className="card research-section"><h3 className="card-title">C1 크립토 추세 · 현재 적용 규칙</h3><div className="research-summary-row">
        <div><span className="muted-note">신호 주기</span><strong>{params?.tf_minutes ?? "—"}분</strong></div>
        <div><span className="muted-note">EMA 교차</span><strong>{params?.ema_fast ?? "—"} / {params?.ema_slow ?? "—"}</strong></div>
        <div><span className="muted-note">샹들리에 손절</span><strong>{params?.stop_atr ?? "—"} ATR</strong></div>
        <div><span className="muted-note">슬리브별 노출</span><strong>{params?.exposure ?? "—"}배</strong></div>
      </div><p className="muted-note">BTC·ETH·SOL 독립 슬리브의 모의 운용입니다. 뉴스·매크로 지표는 자문 입력이며 C1의 진입 차단 조건에 포함되지 않습니다.</p></section>}
      {!profile?.available && <div className="notice-card research-section">활성 운용 프로필을 확인하고 있습니다.</div>}
      {/* V2 Hard Risk Safeguards Panel */}
      {profile?.available && !trend && (
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <Zap size={16} color="var(--accent)" />
          <h3 className="card-title">KORU-v2 5대 하드 리스크 방어벽 (Hard Risk Safeguards)</h3>
          <span className="card-title-meta">알고리즘 엔진 내장 강제 규칙</span>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 16 }}>
          {/* 1. Daily Loss Cap */}
          <div className="metric-tile" style={{ padding: "12px 14px" }}>
            <div className="metric-tile-head">
              <span className="metric-tile-label">일일 손실 한도 (Daily Cap)</span>
              <Percent size={14} color="var(--accent)" />
            </div>
            <div className={`metric-tile-value ${util?.is_loss_limit_breached ? "danger" : (util?.daily_pnl_pct ?? 0) < 0 ? "warning" : "success"}`}>
              {util?.daily_pnl_pct != null ? `${util.daily_pnl_pct >= 0 ? "+" : ""}${util.daily_pnl_pct}%` : "0.0%"}
            </div>
            <div className="metric-tile-sub">
              한도: <strong>{hard?.daily_loss_limit_pct ?? -4.0}%</strong> · 잔여 버퍼: <strong>{util?.remaining_loss_budget_pct ?? 4.0}%</strong>
            </div>
          </div>

          {/* 2. 2.5% Stop Floor */}
          <div className="metric-tile" style={{ padding: "12px 14px" }}>
            <div className="metric-tile-head">
              <span className="metric-tile-label">최소 손절 바닥 (Stop Floor)</span>
              <ShieldCheck size={14} color="var(--green)" />
            </div>
            <div className="metric-tile-value success" style={{ fontSize: 18 }}>
              {hard?.min_stop_floor_pct ?? 2.5}%
            </div>
            <div className="metric-tile-sub">
              최소 거리 적용 · <strong>{hard?.capital_risk_per_trade_pct ?? "—"}% 자본 리스크 상한</strong>
            </div>
          </div>

          {/* 3. Breakeven Stop Promotion */}
          <div className="metric-tile" style={{ padding: "12px 14px" }}>
            <div className="metric-tile-head">
              <span className="metric-tile-label">본전 스탑 승격 (Breakeven)</span>
              <CheckCircle2 size={14} color="var(--accent)" />
            </div>
            <div className="metric-tile-value" style={{ fontSize: 18, color: "var(--accent)" }}>
              +{hard?.breakeven_promotion_trigger_pct ?? 0.8}% 발동
            </div>
            <div className="metric-tile-sub">
              이익 도달 시 손절가 <strong>+{hard?.breakeven_stop_buffer_pct ?? 0.1}% 버퍼</strong>로 승격
            </div>
          </div>

          {/* 4. Portfolio Sector Cap */}
          <div className="metric-tile" style={{ padding: "12px 14px" }}>
            <div className="metric-tile-head">
              <span className="metric-tile-label">섹터 포지션 캡 (Sector Cap)</span>
              <Layers size={14} color="var(--purple)" />
            </div>
            <div className="metric-tile-value" style={{ fontSize: 18, color: "var(--purple)" }}>
              최대 {hard?.portfolio_sector_cap ?? 1}개 / 섹터
            </div>
            <div className="metric-tile-sub">
              섹터별 한도 · {hard?.macro_alignment_threshold == null ? "매크로 자문 모드" : `매크로 역행 차단 (${hard.macro_alignment_threshold})`}
            </div>
          </div>
        </div>

        {/* Safeguard Descriptions & Strategy Policy */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: "var(--text-sm)", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <strong style={{ color: "var(--text-primary)" }}>활성 전략 수트 (Strategy Suite):</strong>
            <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
              {v2Controls?.active_strategies.map(strategy =>
                <span key={strategy} className="badge badge-blue">Strategy {strategy}</span>)}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <strong style={{ color: "var(--text-muted)" }}>비활성 전략 (Safety Lockout):</strong>
            <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
              {v2Controls?.disabled_strategies.map(item =>
                <span key={item.strategy} className="badge badge-yellow" title={item.reason}>
                  Strategy {item.strategy}
                </span>)}
            </div>
          </div>
        </div>
      </div>

      )}
      {/* Real-time Guards Grid */}
      <div className="grid grid-3" style={{ marginBottom: "var(--space-4)" }}>
        <div
          className="card"
          style={{
            borderColor: killSwitch ? "var(--red)" : "var(--border)",
            background: killSwitch ? "rgba(244, 63, 94, 0.06)" : "var(--bg-card)",
          }}
        >
          <div className="stat-label">비상 킬스위치 (Kill Switch)</div>
          <div
            className="stat-value"
            style={{
              color: killSwitch ? "var(--red)" : "var(--green)",
              fontSize: 20,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {killSwitch ? <ShieldAlert size={20} color="var(--red)" /> : <ShieldCheck size={20} color="var(--green)" />}
            <span>{killSwitch == null ? "상태 확인 중" : killSwitch ? "차단 가동 (HALTED)" : "신규 진입 조건 평가"}</span>
          </div>
          <div className="muted-note">
            {killSwitch == null ? "킬스위치 근거를 기다리고 있습니다" : killSwitch ? "신규 진입 차단 상태" : "신규 진입은 활성 전략의 조건에 따라 평가됩니다"}
          </div>
        </div>

        <div
          className="card"
          style={{
            borderColor: macroGuard.active ? "var(--orange)" : "var(--border)",
          }}
        >
          <div className="stat-label">거시경제 가드 (Macro Guard)</div>
          <div
            className="stat-value"
            style={{
              color: macroGuard.active ? "var(--orange)" : "var(--green)",
              fontSize: 20,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {macroGuard.active ? <ShieldAlert size={20} color="var(--orange)" /> : <ShieldCheck size={20} color="var(--green)" />}
            <span>{trend ? "자문 입력" : macroGuard.active == null ? "상태 확인 중" : macroGuard.active ? "위험 차단 (ACTIVE)" : "정상 허용 (CLEAR)"}</span>
          </div>
          <div className="muted-note">
            {trend ? "C1 진입 차단에 적용되지 않습니다" : macroGuard.reason || "고영향 경제 지표 및 CPI/FOMC 변동성 회피 가드"}
          </div>
        </div>

        <div className="card">
          <div className="stat-label">VKOSPI 옵션 변동성 가드</div>
          <div className="stat-value" style={{ fontSize: 20, marginTop: 4 }}>
            <span className={`badge ${vkospiGuard.is_spike ? "badge-red" : "badge-green"}`}>
              {trend ? "현재 프로필 미적용" : vkospiGuard.is_spike == null ? "상태 확인 중" : vkospiGuard.is_spike ? "IV 급등 감지 (디레버리징)" : "정상 범위 (CLEAR)"}
            </span>
          </div>
          <div className="muted-note">
            {trend ? "크립토 C1은 VKOSPI 축소 규칙을 사용하지 않습니다" : `KIS 옵션 내재변동성 z-score ${vkospiGuard.z_score == null ? "—" : vkospiGuard.z_score.toFixed(2)}` }
          </div>
        </div>
      </div>

      {/* Tracking Error Analysis */}
      <div className="card" style={{ marginBottom: "var(--space-4)" }}>
        <div className="card-title-row">
          <h3 className="card-title">가상 vs 실거래 추적 오차 (Tracking Error)</h3>
          <span className="card-title-meta">Paper vs Live 체결 슬리피지 & 지연 감사</span>
        </div>
        {trackingError?.fills?.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>체결 시각</th>
                <th>신호 ID</th>
                <th>가상 체결가 (Paper)</th>
                <th>실제 체결가 (Live)</th>
                <th>슬리피지 (Slippage)</th>
                <th>수수료 차이</th>
                <th>지연 시간 (Latency)</th>
              </tr>
            </thead>
            <tbody>
              {trackingError.fills.slice(0, 50).map((f: any, i: number) => {
                const slip = Math.abs(Number(f.slippage_bps || 0));
                const slipColor = slip > 6.75 ? "var(--red)" : slip > 4.5 ? "var(--yellow)" : "var(--green)";
                return (
                  <tr key={i}>
                    <td style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                      {fmtKst(f.ts)}
                    </td>
                    <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                      {f.signal_id?.slice(0, 12)}
                    </td>
                    <td>{formatPrice(f.paper_px)}</td>
                    <td>{formatPrice(f.live_px)}</td>
                    <td
                      style={{
                        color: slipColor,
                        fontWeight: 800,
                      }}
                    >
                      {Number(f.slippage_bps || 0).toFixed(2)} bps
                    </td>
                    <td>{(Number(f.live_fee || 0) - Number(f.paper_fee || 0)).toFixed(4)} USDT</td>
                    <td style={{ fontWeight: 600 }}>{f.latency_ms ?? "—"} ms</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ color: "var(--text-muted)", padding: "32px 20px", textAlign: "center", fontStyle: "italic" }}>
            최근 체결된 추적 오차 비교 데이터가 없습니다 (정상 연동 중)
          </div>
        )}
      </div>

      {/* Risk Events History */}
      <div className="card">
        <div className="card-title-row">
          <h3 className="card-title">리스크 이벤트 발생 이력 (Risk Events)</h3>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: "var(--text-sm)" }}>
              조회 범위:
              <select value={hours} onChange={(e) => setHours(+e.target.value)}>
                <option value={24}>최근 24시간</option>
                <option value={72}>최근 3일</option>
                <option value={168}>최근 7일</option>
              </select>
            </label>
          </div>
        </div>
        {events.length === 0 ? (
          <div style={{ color: "var(--text-muted)", padding: "32px 20px", textAlign: "center", fontStyle: "italic" }}>
            기록된 위험 이벤트가 없습니다 (리스크 통제 안전 상태)
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>발생 시각</th>
                <th>이벤트 유형</th>
                <th>위험 등급</th>
                <th>상세 내용</th>
                <th>신호 ID</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i}>
                  <td style={{ color: "var(--text-secondary)", fontSize: "var(--text-sm)" }}>
                    {fmtKst(e.ts)}
                  </td>
                  <td>
                    <span className="badge badge-blue">{e.kind}</span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        e.severity === "critical"
                          ? "badge-red"
                          : e.severity === "warning"
                          ? "badge-yellow"
                          : "badge-blue"
                      }`}
                    >
                      {e.severity === "critical"
                        ? "심각 (Critical)"
                        : e.severity === "warning"
                        ? "경고 (Warning)"
                        : "정보 (Info)"}
                    </span>
                  </td>
                  <td style={{ fontSize: 12.5 }}>{e.detail}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                    {e.signal_id?.slice(0, 12) ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
